import { Loader2 } from 'lucide-react';
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { usePartnerProfile } from '../../../hooks/usePartnerSettings';
import { SETTINGS_STRINGS } from '../../../lib/constants';
import { ColorFieldGrid } from './ColorFieldGrid';
import { SliderField } from './SliderField';
import { TextField } from './TextField';
import { ThemePreview } from './ThemePreview';
import styles from './ThemeTab.module.css';
import { buildGoogleFontUrl, getFirstFontFamily } from './font-helpers';
import {
  COLOR_STYLE_FIELDS,
  CHART_COLOR_FIELDS,
  PREVIEW_FONT_BODY_FALLBACK,
  PREVIEW_FONT_HEADING_FALLBACK,
} from './theme-constants';
import { mergePreviewThemeStyles, resolveThemeToken } from './theme-helpers';
import type { ThemeConfig, ThemeStyleToken } from './theme-types';

export const ThemeTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [formData, setFormData] = React.useState<{ themeConfig?: Partial<ThemeConfig> }>({});

  React.useEffect(() => {
    if (partner) {
      setFormData({ themeConfig: partner.themeConfig });
    }
  }, [partner]);

  const themeStyles = formData.themeConfig?.styles;
  const token = (key: ThemeStyleToken) => resolveThemeToken(themeStyles, key);
  const previewDark = !!formData.themeConfig?.darkMode;
  const previewMerged = React.useMemo(
    () => mergePreviewThemeStyles(themeStyles, previewDark),
    [themeStyles, previewDark],
  );
  const previewToken = (key: ThemeStyleToken) => previewMerged[key] ?? '';
  const bodyFontStack = token('font-body');
  const headingFontStack = token('font-heading');

  React.useEffect(() => {
    const fonts = [bodyFontStack, headingFontStack]
      .map(getFirstFontFamily)
      .filter((f): f is string => Boolean(f));

    const uniqueFonts = [...new Set(fonts)];
    if (uniqueFonts.length === 0) return;

    const linkId = 'theme-preview-fonts';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    const href = buildGoogleFontUrl(uniqueFonts);

    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;

    return () => {
      link?.remove();
    };
  }, [bodyFontStack, headingFontStack]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({ themeConfig: formData.themeConfig });
  };

  const handleThemeStyleChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      themeConfig: {
        ...(prev.themeConfig || {}),
        styles: {
          ...(prev.themeConfig?.styles || {}),
          [key]: value,
        },
      },
    }));
  };

  if (isLoading) {
    return <Loader2 className={styles.loading} aria-label="Loading theme settings" />;
  }

  const strings = SETTINGS_STRINGS.THEME_CONFIGURATION;
  const radiusNumeric = parseFloat(token('radius')) || 0.625;

  return (
    <section className={styles.themeEditor}>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit}>
            <fieldset>
              <legend>{strings.COLOR_STYLES_LABEL}</legend>
              <ColorFieldGrid
                fields={COLOR_STYLE_FIELDS}
                resolveToken={token}
                onChange={handleThemeStyleChange}
              />
            </fieldset>

            <fieldset>
              <legend>Chart Colors</legend>
              <ColorFieldGrid
                fields={CHART_COLOR_FIELDS}
                resolveToken={token}
                onChange={handleThemeStyleChange}
              />
            </fieldset>

            <fieldset>
              <legend>Embedded SDK</legend>
              <label className={styles.embeddedDarkMode}>
                <input
                  type="checkbox"
                  checked={previewDark}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      themeConfig: {
                        ...(prev.themeConfig || {}),
                        darkMode: e.target.checked,
                      },
                    }))
                  }
                />
                Use dark theme for embedded SDK (live preview uses the same palette)
              </label>
            </fieldset>

            <fieldset className={styles.typography}>
              <legend>Typography</legend>
              <TextField
                id="font-body"
                label="Body Font"
                value={token('font-body')}
                placeholder="Inter, system-ui, sans-serif"
                onChange={(value: string) => handleThemeStyleChange('font-body', value)}
                style={{ fontFamily: token('font-body') || PREVIEW_FONT_BODY_FALLBACK }}
              />
              <TextField
                id="font-heading"
                label="Heading Font"
                value={token('font-heading')}
                placeholder="Plus Jakarta Sans, system-ui, sans-serif"
                onChange={(value: string) => handleThemeStyleChange('font-heading', value)}
                style={{ fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK }}
              />
            </fieldset>

            <fieldset>
              <legend>{strings.BORDER_RADIUS_LABEL}</legend>
              <SliderField
                label="Radius"
                value={radiusNumeric}
                min={0}
                max={2}
                step={0.125}
                unit="rem"
                minHint="0rem (sharp)"
                maxHint="2rem (very round)"
                onChange={(value: number) => handleThemeStyleChange('radius', `${value}rem`)}
              />
            </fieldset>

            <footer>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (partner) {
                    setFormData({ themeConfig: partner.themeConfig });
                  }
                }}
                disabled={isUpdating}>
                {SETTINGS_STRINGS.ORGANIZATION_PROFILE.CANCEL_BUTTON}
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader2 className={styles.spinner} />
                    Saving...
                  </>
                ) : (
                  SETTINGS_STRINGS.ORGANIZATION_PROFILE.SAVE_BUTTON
                )}
              </Button>
            </footer>
          </form>
        </CardContent>
      </Card>

      <ThemePreview resolveToken={previewToken} darkChrome={previewDark} />
    </section>
  );
};
